/* eslint-disable camelcase */
import {useMutation, useQuery} from '@tanstack/react-query'
import {camelCaseObjectKeys} from '../../../api/helpers'
import SERVICES from '../../../config/services'

type OrderType = 'buy' | 'rent'

interface Payload {
  order_type?: OrderType
  product_set_ids: string[]
  apply_account_balance: boolean
  shipping_type: null
  is_card_on_file_charge: boolean
  card_expiration?: string
  paypage_registration_id?: string
}

type Totals = {
  order_item_ids: string[]
  subtotal: number
  subtotal_with_tax: number
  tax: number
  total: number
}

type ConstructPayloadArgs = {
  orderType?: OrderType
  productSetIds: string[]
  shouldApplyAccountBalance: boolean
  cardExpiration?: string
  paypageId?: string
}

export const constructCheckoutPayload = ({
  orderType,
  productSetIds,
  shouldApplyAccountBalance,
  cardExpiration,
  paypageId,
}: ConstructPayloadArgs): Payload => {
  return {
    order_type: orderType,
    product_set_ids: productSetIds,
    apply_account_balance: shouldApplyAccountBalance,
    shipping_type: null,
    is_card_on_file_charge: !(cardExpiration && paypageId),
    card_expiration: cardExpiration,
    paypage_registration_id: paypageId,
  }
}

// TODO: Keeping these separate until they're separated into their own endpoints
export const useTotals: any = (orderId: number, payload: Payload) => {
  return useQuery(
    ['checkoutTotals', orderId, {payload}],
    async () => {
      const response = await fetch(
        `${SERVICES.TBT_API_URL}/v1/orders/${orderId}/items?dry-run=True`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      )

      if (!response.ok) {
        throw new Error('Error fetching totals')
      }

      const totals = (await response.json()) as Totals

      return camelCaseObjectKeys(totals)
    },
    {
      cacheTime: 0, // this type of data we do not want to cache and always want fresh
      enabled: !!orderId,
    },
  )
}

export const useCheckout: any = (orderId: number) => {
  return useMutation(async (payload: Payload) => {
    const response = await fetch(
      `${SERVICES.TBT_API_URL}/v1/orders/${orderId}/items?dry-run=False`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    )

    if (!response.ok) {
      throw new Error('Error checking out')
    }

    return response
  })
}
